import type { CandlestickData, UTCTimestamp } from 'lightweight-charts'
import type { TF } from '../types'

export type RawCandle = {
  open_time: string | number | Date
  open: number | string
  high: number | string
  low: number | string
  close: number | string
  volume?: number | string | null
}

// Conversão robusta para segundos UTC a partir de ISO/ms/s
export function toUTCSec(ts: string | number | Date): number | null {
  if (typeof ts === 'number') {
    // se for em ms, dividir; se já for segundos, retorna
    return ts > 1e12 ? Math.floor(ts / 1000) : Math.floor(ts)
  }
  if (ts instanceof Date) return Math.floor(ts.getTime() / 1000)
  const t = Date.parse(String(ts))
  if (Number.isNaN(t)) return null
  return Math.floor(t / 1000)
}

export function tfToSeconds(tf: TF): number {
  switch (tf) {
    case '5': return 5 * 60
    case '15': return 15 * 60
    case '30': return 30 * 60
    case '60': return 60 * 60
    case '240': return 4 * 60 * 60
    case '720': return 12 * 60 * 60
    case 'D': return 24 * 60 * 60
    case 'W': return 7 * 24 * 60 * 60
    case 'M': default: return 30 * 24 * 60 * 60 // aproximação segura para bucketização visual
  }
}

export function bucketStartSec(tsSec: number, resSec: number): number {
  return Math.floor(tsSec / resSec) * resSec
}

export function sanitizeCandle(c: RawCandle): { open: number; high: number; low: number; close: number } | null {
  const open = +c.open, high = +c.high, low = +c.low, close = +c.close
  if (!Number.isFinite(open + high + low + close)) return null
  return { open, high: Math.max(high, open, close), low: Math.min(low, open, close), close }
}

// Normaliza, alinha e agrega candles por bucket (UTC) garantindo ordem crescente e sem duplicatas.
export function normalizeCandles(raw: RawCandle[] | null | undefined, tf: TF): CandlestickData[] {
  if (!raw || !Array.isArray(raw) || raw.length === 0) return []
  const resSec = tfToSeconds(tf)

  // Map por bucket para agregar e deduplicar
  const byBucket = new Map<number, { time: number; open: number; high: number; low: number; close: number }>()

  function floorWeekUTC(tsSec: number): number {
    const d = new Date(tsSec * 1000)
    const dow = (d.getUTCDay() + 6) % 7 // segunda=0
    const flo = Date.UTC(d.getUTCFullYear(), d.getUTCMonth(), d.getUTCDate(), 0, 0, 0, 0) / 1000
    return flo - dow * 86400
  }
  function floorMonthUTC(tsSec: number): number {
    const d = new Date(tsSec * 1000)
    return Date.UTC(d.getUTCFullYear(), d.getUTCMonth(), 1, 0, 0, 0, 0) / 1000
  }

  for (const c of raw) {
    const tsSec = toUTCSec(c.open_time)
    if (tsSec == null) continue
    let bucket = bucketStartSec(tsSec, resSec)
    if (tf === 'W') bucket = floorWeekUTC(tsSec)
    if (tf === 'M') bucket = floorMonthUTC(tsSec)
    const s = sanitizeCandle(c)
    if (!s) continue

    const prev = byBucket.get(bucket)
    if (!prev) {
      byBucket.set(bucket, { time: bucket, open: s.open, high: s.high, low: s.low, close: s.close })
    } else {
      prev.high = Math.max(prev.high, s.high)
      prev.low = Math.min(prev.low, s.low)
      prev.close = s.close
    }
  }

  // Ordena por tempo crescente e converte para o tipo do gráfico
  return Array.from(byBucket.values())
    .sort((a, b) => a.time - b.time)
    .map((b) => ({ time: b.time as UTCTimestamp, open: b.open, high: b.high, low: b.low, close: b.close }))
}

// Normaliza volumes: agrega por bucket, soma volumes e deriva cor (alta/baixa) pelo candle agregado
export function normalizeVolumes(
  raw: RawCandle[] | null | undefined,
  tf: TF,
): Array<{ time: UTCTimestamp; value: number; color?: string }> {
  if (!raw || !Array.isArray(raw) || raw.length === 0) return []
  const resSec = tfToSeconds(tf)

  type Agg = { vol: number; firstOpen: number | null; lastClose: number | null }
  const map = new Map<number, Agg>()

  for (const c of raw) {
    const tsSec = toUTCSec(c.open_time)
    if (tsSec == null) continue
    const bucket = (tf === 'W' || tf === 'M') ? tsSec : bucketStartSec(tsSec, resSec)
    const v = c.volume == null ? 0 : +c.volume
    if (!Number.isFinite(v)) continue
    const open = +c.open
    const close = +c.close

    const prev = map.get(bucket) || { vol: 0, firstOpen: null, lastClose: null }
    prev.vol += v
    if (prev.firstOpen == null && Number.isFinite(open)) prev.firstOpen = open
    if (Number.isFinite(close)) prev.lastClose = close
    map.set(bucket, prev)
  }

  return Array.from(map.entries())
    .sort((a, b) => a[0] - b[0])
    .map(([t, agg]) => {
      let color: string | undefined
      if (agg.firstOpen != null && agg.lastClose != null) {
        // Verde se o candle agregado fechou acima/igual à abertura, vermelho caso contrário
        color = agg.lastClose >= agg.firstOpen ? '#22c55e' : '#ef4444'
      }
      return { time: t as UTCTimestamp, value: agg.vol, color }
    })
}

// Heurística de formato de preço (precision/minMove) baseada nos dados normalizados
export function detectPriceFormat(candles: CandlestickData[]): { precision: number; minMove: number } | null {
  if (!candles || candles.length < 2) return null
  let maxDec = 0
  const closes: number[] = []
  for (const c of candles.slice(-Math.min(300, candles.length))) {
    closes.push(+c.close)
    for (const v of [c.open, c.high, c.low, c.close]) {
      const s = String(+v)
      const i = s.indexOf('.')
      const d = i >= 0 ? (s.length - i - 1) : 0
      if (d > maxDec) maxDec = d
    }
  }
  const med = medianSafe(closes)
  // Mínimos recomendados por magnitude do preço (evitar corpo 0 por arredondamento)
  let minRecommended = 2
  if (med < 1000) minRecommended = 3
  if (med < 100) minRecommended = 4
  if (med < 10) minRecommended = 4
  if (med < 1) minRecommended = 6
  const precision = Math.min(8, Math.max(minRecommended, maxDec))
  const minMove = Number((1 / Math.pow(10, precision)).toFixed(precision))
  return { precision, minMove }
}

function medianSafe(arr: number[]): number {
  const a = arr.filter((x) => Number.isFinite(x)).sort((x, y) => x - y)
  if (!a.length) return 0
  const n = a.length
  const mid = Math.floor(n / 2)
  return n % 2 ? a[mid] : (a[mid - 1] + a[mid]) / 2
}

// Validações rápidas para debug (ativar com localStorage.setItem('debug:candles','1'))
export function devValidateCandles(tf: TF, normalized: CandlestickData[]): void {
  try {
    if (typeof window === 'undefined') return
    const dbg = (window.localStorage?.getItem('debug:candles') === '1')
    if (!dbg) return
    const times = normalized.map(c => Number(c.time))
    const sortedAsc = times.every((t, i, a) => i === 0 || a[i - 1] < t)
    const dups = times.length - new Set(times).size
    const resSec = tfToSeconds(tf)
    const isWM = tf === 'W' || tf === 'M'
    const misaligned = isWM ? 0 : times.filter(t => (t % resSec) !== 0).length
    const badOhlc = normalized.filter(c => !(+c.low <= Math.min(+c.open, +c.close) && +c.high >= Math.max(+c.open, +c.close))).length
    let gaps = 0
    if (!isWM) {
      for (let i = 1; i < times.length; i++) if ((times[i] - times[i - 1]) !== resSec) gaps++
    }
    console.log('[candles:dev-check]', { tf, sortedAsc, dups, misaligned, gaps, badOhlc, count: normalized.length })
  } catch {}
}

// Remove candles evidentemente inválidos que distorcem a escala (ex.: spikes de preço que comprimem o gráfico)
export function filterOutliers(candles: CandlestickData[], tf?: TF): CandlestickData[] {
  try {
    // Para timeframes altos (W/M) n��o aplicamos filtro de outliers:
    // o n��mero de candles ��� pequeno e qualquer remo����o gera gaps visuais
    // e inconsist��ncia com a base (sa��de/ohlcv_candles).
    if (tf === 'W' || tf === 'M') return candles

    if (!candles || candles.length < 10) return candles
    const preferred = ((): number => {
      switch (tf) {
        case '5': return 150
        case '15': return 170
        case '30': return 190
        default: return 220
      }
    })()
    const window = Math.min(candles.length, preferred * 2)
    const slice = candles.slice(-window)
    const closes = slice.map(c => Number(c.close)).filter(v => Number.isFinite(v))
    if (closes.length < 10) return candles
    const med = medianSafe(closes)
    if (!Number.isFinite(med) || Math.abs(med) < 1e-9) return candles
    const devs = closes.map(v => Math.abs(v - med))
    const mad = Math.max(1e-9, medianSafe(devs))
    const k = 6.0
    const lo = med - k * mad
    const hi = med + k * mad
    const min = Math.min(lo, med * 0.3)
    const max = Math.max(hi, med * 3.0)
    const filtered = candles.filter(c => {
      const v = Number(c.close)
      return Number.isFinite(v) && v >= min && v <= max
    })
    if (filtered.length < Math.max(10, Math.floor(candles.length * 0.8))) return candles
    return filtered
  } catch {
    return candles
  }
}
